import moment from 'moment';
import downloadFile from 'downloadjs';
import { isNumber, truncate } from 'lodash';
import * as Sentry from '@sentry/browser';

import { createReducer } from 'utils/redux';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger, info } from 'redux-store/reducers/common-reducer';

import { API, get, post } from 'utils/api';
import { logError } from 'utils/log';
import { FORMAT_MAP } from 'utils/reporting';
import { __t, applyTranslations } from 'utils/language';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import type {
  Action,
  CustomReportDownloadType,
  CustomReportOffer,
  Dispatch,
  GetState,
  ThunkAction
} from 'types';

applyTranslations(require('./campaign-custom-report.i18n.json'));

const { REPORTS } = API.END_POINTS;

const RECEIVE_DATA = 'customReport/RECEIVE_DATA';
const RESET = 'customReport/RESET';
const LOADING_STATE_CHANGED = 'customReport/LOADING_STATE_CHANGED';
const DOWNLOADING_STATE_CHANGED = 'customReport/DOWNLOADING_STATE_CHANGED';

type State = {
  groupedDeliverables: Array<CustomReportOffer>,
  isLoading: boolean,
  isDownloading: boolean
};

type ReportDownloadPayload = {
  downloadType?: string,
  checkedIds?: Array<number>,
  uncheckedIds?: Array<number>
};

const initialState: State = {
  groupedDeliverables: [],
  isLoading: false,
  isDownloading: false
};

export function resetCampaignCustomReport(): Action {
  return {
    type: RESET
  };
}

function setLoadingState(state: boolean): Action {
  return {
    type: LOADING_STATE_CHANGED,
    payload: {
      state
    }
  };
}

function setDownloadingState(isDownloading: boolean): Action {
  return {
    type: DOWNLOADING_STATE_CHANGED,
    payload: {
      isDownloading
    }
  };
}

function receiveData(groupedDeliverables: Array<CustomReportOffer>): Action {
  return {
    type: RECEIVE_DATA,
    payload: {
      groupedDeliverables
    }
  };
}

export function fetchCustomReportDeliverables(campaignId: number): ThunkAction {
  return async (dispatch: Dispatch) => {
    const url = `${REPORTS.customReportV2(campaignId)}`;

    dispatch(showLoading(url));
    dispatch(setLoadingState(true));

    try {
      const response = await get(url, undefined,
        { suppressGlobalNotification: true }) as { data: { data: Array<CustomReportOffer>} };
      const { data } = response.data;

      dispatch(receiveData(data));
    } catch (error) {
      logError(error);
      dispatch(danger(__t('campaign_custom_report_reducer.loading_page_error')));
    } finally {
      dispatch(hideLoading(url));
      dispatch(setLoadingState(false));
    }
  };
}

const MAX_CAMPAIGN_REPORT_NAME_LENGTH = 30;
const ASYNC_DOWNLOAD_INTERVAL_TIME = 3000;
let ASYNC_DOWNLOAD_INTERVAL_ID: number;

const clearDownloadInterval = (): void => (
  window.clearInterval(ASYNC_DOWNLOAD_INTERVAL_ID)
);

const downloadCustomReportAsync = (
  campaignId: number,
  taskId: number,
  downloadType: CustomReportDownloadType
): ThunkAction => (
  async (dispatch: Dispatch, getState: GetState) => {
    const campaignName = getState()?.campaignBrief?.brief?.name || '-';
    const date = moment().format('YYYY-MM-DD');
    const campaignShortName = truncate(campaignName, { length: MAX_CAMPAIGN_REPORT_NAME_LENGTH });
    const fileName = `Collabary Campaign Report ${date} (${campaignShortName}).${FORMAT_MAP[downloadType].ext}`;

    const url = REPORTS.customReportV2AsyncDownload(taskId);

    try {
      const response = await get(url, { responseType: 'blob' }, { suppressGlobalNotification: true });

      const { data } = response as { data: Blob };
      downloadFile(data, fileName);

      addUserAction({
        eventCategory: 'campaign-report',
        eventAction: FORMAT_MAP[downloadType].trackingEvent
      });
    } catch (err) {
      dispatch(setDownloadingState(false));
      logError(err);
      dispatch(danger(__t('campaign_custom_report_reducer.loading_report_error')));
    } finally {
      dispatch(setDownloadingState(false));
    }
  }
);

// Removing all isNewVersion condition as this flag will be deprecated soon and generateCustomReportAsync is being
// called only for new version and should not be called for old version api's
export const generateCustomReportAsync = (
  campaignId: number,
  checkedIds: Array<number> | undefined | null,
  downloadType: CustomReportDownloadType
): ThunkAction => (
  async (dispatch: Dispatch) => {
    const taskIdUrl = REPORTS.customReportV2AsyncGenerate(campaignId);

    const payload: ReportDownloadPayload = {
      downloadType,
      checkedIds: checkedIds?.length ? checkedIds : undefined,
      uncheckedIds: undefined
    };

    try {
      dispatch(setDownloadingState(true));
      dispatch(info(__t('campaign_custom_report_reducer.async_download_notification'), { hideDuration: 5000 }));

      const taskIdResponse = await post(taskIdUrl, {
        data: payload
      }, { suppressGlobalNotification: true });

      const taskId = taskIdResponse.data?.taskId;

      if (taskId) {
        const statusUrl = REPORTS.customReportV2AsyncCheckStatus(taskId);

        if (isNumber(ASYNC_DOWNLOAD_INTERVAL_ID)) {
          clearDownloadInterval();
        }

        // keep calling endpoint until status is 'ready' or 'error'
        ASYNC_DOWNLOAD_INTERVAL_ID = window.setInterval(async () => { // eslint-disable-line consistent-return
          const statusResponse = await get(statusUrl);
          const status = statusResponse.data?.status;

          // COLLAB-12459
          if (status === 'ready') {
            clearDownloadInterval();
            return dispatch(downloadCustomReportAsync(campaignId, taskId, downloadType));
          }

          if (status === 'error') {
            dispatch(setDownloadingState(false));
            clearDownloadInterval();
            logError(__t('campaign_custom_report_reducer.generation_report_error'));
            dispatch(danger(__t('campaign_custom_report_reducer.generation_report_error')));
          }
        }, ASYNC_DOWNLOAD_INTERVAL_TIME);
      } else {
        dispatch(setDownloadingState(false));
        logError('campaign_custom_report_reducer.no_task_id_error');
        dispatch(danger(__t('campaign_custom_report_reducer.no_task_id_error',
          { downloadType: downloadType === 'presentation' ? 'PowerPoint' : 'Excel' })));
      }
    } catch (err) {
      dispatch(setDownloadingState(false));
      logError(err);
      if (err.toString().includes('Network Error')) {
        dispatch(danger(__t('campaign_custom_report_reducer.network_error')));
      } else {
        dispatch(danger(__t('campaign_custom_report_reducer.loading_report_error')));
        Sentry.captureException(new Error(`Error while downloading report: ${err}`));
      }
    }
  }
);

export default createReducer<State, any>('campaignCustomReport', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING_STATE_CHANGED]: (state) => (
    state
  ),
  [DOWNLOADING_STATE_CHANGED]: (state, { isDownloading }) => ({
    ...state,
    isDownloading
  }),
  [RECEIVE_DATA]: (state, { groupedDeliverables }) => ({
    ...state,
    groupedDeliverables
  })
});
